import { Controller } from "@hotwired/stimulus";
import { ThemeEvents } from "@theme/events";

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

class AddToCartController extends Controller {
  static targets = ["addToCartForm", "quantityInput", "addToCartTextError"];
  static outlets = ["miniCart", "discount-kit-volume-tiers"];
  static values = {
    successRedirectUrl: String,
  };

  /** @type {number | undefined} */
  #timeout;

  get #inputValue() {
    return parseInt(this.quantityInputTarget.value);
  }

  get #canIncrement() {
    return !this.max || this.#inputValue < this.max;
  }

  get #canDecrement() {
    return this.#inputValue > this.min;
  }

  #setMinAndMaxValues() {
    if (this.hasQuantityInputTarget) {
      this.min = parseInt(this.quantityInputTarget.getAttribute("min"));
      this.max = parseInt(this.quantityInputTarget.getAttribute("max"));
      this.step = parseInt(this.quantityInputTarget.getAttribute("step"));
    }
  }

  connect() {
    this.#setMinAndMaxValues();
  }

  setDiscountKitVolumeTiersQuantity(e) {
    if (this.hasDiscountKitVolumeTiersOutlet) {
      this.discountKitVolumeTiersOutlet.setActiveTierByTierBuy(e.target.value);
    }
  }

  incrementQuantity() {
    if (this.hasQuantityInputTarget && this.#canIncrement) {
      this.quantityInputTarget.value = this.#inputValue + this.step;
    }
    this.setDiscountKitVolumeTiersQuantity({
      target: { value: this.#inputValue },
    });
  }

  decrementQuantity() {
    if (this.hasQuantityInputTarget && this.#canDecrement) {
      this.quantityInputTarget.value = this.#inputValue - this.step;
    }
    this.setDiscountKitVolumeTiersQuantity({
      target: { value: this.#inputValue },
    });
  }

  setQuantity(e) {
    if (this.hasQuantityInputTarget && this.#canIncrement) {
      this.quantityInputTarget.value = e.target.dataset.tierBuy;
    }
  }

  async add(event) {
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const form = this.addToCartFormTarget;

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch(form.action, {
        method: form.method,
        body: formData,
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response.ok || response.status === 422) {
        response.json().then((data) => {
          let errorMessage = data.errors ?? data.description ?? data.message;

          if (this.hasAddToCartTextErrorTarget) {
            const addToCartTextError = this.addToCartTextErrorTarget;
            addToCartTextError.classList.remove("tw-hidden");

            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = errorMessage;
            } else {
              const newTextNode = document.createTextNode(errorMessage);
              addToCartTextError.appendChild(newTextNode);
            }

            //this.#setLiveRegionText(errorMessage);

            if (this.#timeout) clearTimeout(this.#timeout);
            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add("tw-hidden");
              //this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }
        });
      } else {
        // Clear the form inputs after a successful add to cart
        form.reset();

        document.dispatchEvent(
          new Event(ThemeEvents.cartUpdate, { bubbles: true }),
        );

        // Update and show the mini cart
        return this.miniCartOutlet.updateCart({
          event,
          detail: {
            quantity: formData.get("quantity"),
            id: formData.get("id"),
          },
        });
      }
    } catch (error) {
      console.error("Network error", error);
    }
  }
}

Stimulus.register("addToCart", AddToCartController);
